import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Heart, User, MapPin, Menu, ChevronDown, Truck, Phone, LogOut } from 'lucide-react';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { fetchCategories } from '../lib/api';

const Header = () => {
  const { totalItems } = useCart();
  const { user, logout } = useAuth();
  const [search, setSearch] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [categories, setCategories] = useState([]);
  const accountRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccount(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/categorie/tous?q=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleLogout = async () => {
    setShowAccount(false);
    await logout();
    navigate('/');
  };

  const isAuthed = user && typeof user === 'object';
  const firstName = isAuthed ? (user.name || user.email).split(' ')[0] : '';

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm" data-testid="site-header">
      {/* Top bar */}
      <div className="bg-[#262626] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5"><Truck size={14} /> Livraison gratuite dès 50€</span>
            <span className="hidden md:flex items-center gap-1.5"><Phone size={14} /> Service client 7j/7</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline">Vendre sur WorldMarket</span>
            <span className="hidden md:inline">Aide</span>
            <span className="flex items-center gap-1">FR <ChevronDown size={12} /></span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <div className="flex items-center">
            <span className="text-3xl font-black tracking-tight">
              <span className="text-[#00847A]">World</span><span className="text-[#FF4D4D]">Market</span>
            </span>
          </div>
        </Link>

        <form onSubmit={handleSearch} className="flex-1 max-w-3xl relative">
          <div className="flex items-stretch border-2 border-[#00847A] rounded-full overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#00847A]/30">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit, une marque..."
              className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11 pl-5 text-sm"
              data-testid="search-input"
            />
            <button type="submit" data-testid="search-submit" className="bg-[#00847A] hover:bg-[#006B63] text-white px-6 flex items-center justify-center">
              <Search size={20} />
            </button>
          </div>
        </form>

        <div className="flex items-center gap-1 shrink-0">
          <button className="hidden md:flex flex-col items-center text-xs text-[#262626] hover:text-[#00847A] px-3 py-2 rounded-lg hover:bg-neutral-50">
            <MapPin size={22} />
            <span className="mt-0.5">Livrer à</span>
          </button>

          <div className="relative" ref={accountRef}>
            {isAuthed ? (
              <button
                onClick={() => setShowAccount((s) => !s)}
                data-testid="account-menu-btn"
                className="flex flex-col items-center text-xs text-[#262626] hover:text-[#00847A] px-3 py-2 rounded-lg hover:bg-neutral-50"
              >
                <User size={22} />
                <span className="mt-0.5 max-w-[80px] truncate">{firstName}</span>
              </button>
            ) : (
              <Link to="/login" data-testid="login-link" className="flex flex-col items-center text-xs text-[#262626] hover:text-[#00847A] px-3 py-2 rounded-lg hover:bg-neutral-50">
                <User size={22} />
                <span className="mt-0.5">Compte</span>
              </Link>
            )}

            {isAuthed && showAccount && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-neutral-200 rounded-xl shadow-lg py-2 z-50" data-testid="account-menu">
                <div className="px-4 py-2 border-b border-neutral-100">
                  <div className="text-sm font-semibold text-[#262626] truncate">{user.name}</div>
                  <div className="text-xs text-[#595959] truncate">{user.email}</div>
                </div>
                <Link to="/panier" onClick={() => setShowAccount(false)} className="block px-4 py-2 text-sm text-[#262626] hover:bg-neutral-50">Mon panier</Link>
                {user.role === 'admin' && (
                  <Link to="/admin" onClick={() => setShowAccount(false)} data-testid="admin-link" className="block px-4 py-2 text-sm font-semibold text-[#00847A] hover:bg-neutral-50">🏬 Entrepôt (Admin)</Link>
                )}
                <button onClick={handleLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#FF4D4D] hover:bg-neutral-50">
                  <LogOut size={16} /> Se déconnecter
                </button>
              </div>
            )}
          </div>

          <button className="hidden md:flex flex-col items-center text-xs text-[#262626] hover:text-[#00847A] px-3 py-2 rounded-lg hover:bg-neutral-50">
            <Heart size={22} />
            <span className="mt-0.5">Favoris</span>
          </button>
          <Link to="/panier" data-testid="cart-link" className="relative flex flex-col items-center text-xs text-[#262626] hover:text-[#00847A] px-3 py-2 rounded-lg hover:bg-neutral-50">
            <div className="relative">
              <ShoppingCart size={22} />
              {totalItems > 0 && (
                <span data-testid="cart-badge" className="absolute -top-2 -right-2 bg-[#FF4D4D] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {totalItems}
                </span>
              )}
            </div>
            <span className="mt-0.5">Panier</span>
          </Link>
        </div>
      </div>

      {/* Categories nav */}
      <nav className="border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#262626] hover:text-[#00847A] shrink-0"
              data-testid="all-categories-btn"
            >
              <Menu size={18} /> Tous les rayons
            </button>
            {categories.slice(0, 8).map((cat) => (
              <Link
                key={cat.id}
                to={`/categorie/${cat.id}`}
                data-testid={`nav-category-${cat.id}`}
                className="px-3 py-3 text-sm text-[#595959] hover:text-[#00847A] whitespace-nowrap shrink-0 relative hover:after:absolute hover:after:bottom-0 hover:after:left-3 hover:after:right-3 hover:after:h-0.5 hover:after:bg-[#00847A]"
              >
                {cat.name}
              </Link>
            ))}
            <Link to="/categorie/tous" className="ml-auto px-3 py-3 text-sm text-[#FF4D4D] font-semibold whitespace-nowrap">Promotions -50%</Link>
          </div>
        </div>

        {showMenu && (
          <div className="absolute left-0 right-0 bg-white border-t border-neutral-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/categorie/${cat.id}`}
                  onClick={() => setShowMenu(false)}
                  className="text-sm text-[#262626] hover:text-[#00847A] py-1"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
